import { useMemo } from 'react';
import {
  usePhoneticLibraryPersonalQuery,
  useUserProjectReplacementsQuery,
} from '@replacements/graphql/operation.generated';
import { useCanReadTeamReplacements } from 'abilities/replacements';
import {
  PhoneticReplacement,
  StudioProjectVersion,
} from '../../../../../server/graphql/__generated.types';
import { EnhancedPhoneticReplacement } from '../../../../store/ReplacementsSlice';

import useEvaluateStudioProjectVersionFeatures from '../../../Studio/useEvaluateStudioProjectVersionFeatures';

const onlyEnabledReplacements = (
  replacements: (PhoneticReplacement | undefined | null)[]
) => {
  return replacements.reduce(
    (filtered: EnhancedPhoneticReplacement[], replacement) => {
      if (replacement?.enabled) {
        filtered.push(replacement);
      }
      return filtered;
    },
    []
  );
};

/**
 * @description helper which will initialize project replacements
 * @note projectId is conditional as its usage references a conditional project object
 */
const useInitializeProjectReplacements = (
  projectId?: string,
  projectStudioVersion?: StudioProjectVersion
): [PhoneticReplacement[], boolean] => {
  const projectVersionFeatures = useEvaluateStudioProjectVersionFeatures({
    studioVersion: projectStudioVersion,
  });

  const canReadTeamReplacements = useCanReadTeamReplacements();

  const {
    loading: phoneticLibraryQueryLoading,
    data: phoneticLibraryQueryData,
  } = usePhoneticLibraryPersonalQuery({
    skip:
      canReadTeamReplacements || projectVersionFeatures.replacementsDisabled,
  });
  const {
    loading: phoneticReplacementsQueryLoading,
    data: phoneticReplacementsQueryData,
  } = useUserProjectReplacementsQuery({
    variables: { inputs: { projectId: projectId! } },
    skip:
      !canReadTeamReplacements ||
      !projectId ||
      projectVersionFeatures.replacementsDisabled,
  });

  const replacements = useMemo(() => {
    if (canReadTeamReplacements) {
      return phoneticReplacementsQueryData
        ? (phoneticReplacementsQueryData?.user?.project
            ?.replacements as PhoneticReplacement[]) || []
        : [];
    }
    return phoneticLibraryQueryData
      ? onlyEnabledReplacements(
          phoneticLibraryQueryData?.user?.phoneticLibrary.replacements_V2
            ?.personal || []
        )
      : [];
  }, [
    phoneticLibraryQueryData,
    phoneticReplacementsQueryData,
    canReadTeamReplacements,
  ]);

  const queryIsLoading =
    phoneticLibraryQueryLoading || phoneticReplacementsQueryLoading;

  return [replacements, queryIsLoading];
};

export default useInitializeProjectReplacements;
